/**
 * App — root router with auth gating and role-based access.
 *
 * Flow:
 *   1. Default entry point → /login (always)
 *   2. Login page shows "Set up BOS" link when onboarding not yet complete
 *   3. /onboarding accessible directly, no auth required; redirects to /login on completion
 *   4. Logged in as admin → full dashboard + admin routes
 *   5. Logged in as user → scoped dashboard (no system config)
 */

import { Suspense, lazy, useState, useCallback } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Rascals } from './pages/Rascals';
import { Layout } from './components/Layout';
import { PageLoader } from './components/LoadingSpinner';
import { FirstRunExperience } from './components/FirstRunExperience';
import { WorkspaceContext, type WorkspaceType } from './hooks/useWorkspace';
import { useProduct } from './context/ProductContext';
import { hosWhatsappManagementPath } from './lib/hosSocialPolicy';

// Lazy-load pages
const Dashboard    = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Office       = lazy(() => import('./pages/Office'));
const SignalDesk   = lazy(() => import('./pages/SignalDesk'));
const Login        = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const Settings     = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const Onboarding   = lazy(() => import('./pages/Onboarding').then((m) => ({ default: m.Onboarding })));
const Calendar     = lazy(() => import('./pages/Calendar'));
const Paperclip    = lazy(() => import('./pages/Paperclip'));
const LinkedIn     = lazy(() => import('./pages/LinkedIn'));
const WhatsApp     = lazy(() => import('./pages/WhatsApp'));
const TaskBoard    = lazy(() => import('./pages/TaskBoard'));
const Canvas       = lazy(() => import('./pages/Canvas'));
const CRM          = lazy(() => import('./pages/CRM'));
const OC           = lazy(() => import('./pages/OC'));
const Board        = lazy(() => import('./pages/Board'));
const JoinMeeting  = lazy(() => import('./pages/JoinMeeting'));
const COO          = lazy(() => import('./pages/COO'));
const AgentWorkspace = lazy(() => import('./pages/AgentWorkspace').then((m) => ({ default: m.OutsiderWorkspace })));
const RascalWorkspace = lazy(() => import('./pages/AgentWorkspace').then((m) => ({ default: m.RascalWorkspace })));
const EmployeeAgents = lazy(() => import('./pages/EmployeeAgents').then((m) => ({ default: m.EmployeeAgents })));
const Health       = lazy(() => import('./pages/Health'));
const HealthHolo   = lazy(() => import('./pages/HealthHolo'));
const HealthJournal = lazy(() => import('./pages/HealthJournal'));
const HealthMedicalRecords = lazy(() => import('./pages/HealthMedicalRecords'));
const HosDashboard   = lazy(() => import('./pages/hos/HosDashboard'));
const HosAssistant   = lazy(() => import('./pages/hos/HosAssistant'));
const HosSocial      = lazy(() => import('./pages/hos/HosSocial'));
const HosAddressBook = lazy(() => import('./pages/hos/HosAddressBook'));
const HosFitness     = lazy(() => import('./pages/hos/HosFitness'));
const HosNutrition   = lazy(() => import('./pages/hos/HosNutrition'));
const HosFinances    = lazy(() => import('./pages/hos/HosFinances'));
const HosSpiritual   = lazy(() => import('./pages/hos/HosSpiritual'));

function getUser(): { role: string } | null {
  try {
    const raw = localStorage.getItem('boss_user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isLoggedIn(): boolean {
  return !!localStorage.getItem('boss_token');
}

/**
 * Requires a valid login token.
 * Redirects to /login if not authenticated.
 * Onboarding check is intentionally removed — the login page
 * surfaces the onboarding link when needed.
 */
function RequireAuth() {
  const location = useLocation();
  const [workspace, setWorkspace] = useState<WorkspaceType>(null);
  const [browserUrl, setBrowserUrl] = useState<string | null>(null);

  const openWorkspace = useCallback((id: string) => {
    setWorkspace(id as WorkspaceType);
  }, []);

  const openBrowser = useCallback((url: string) => {
    setBrowserUrl(url);
    setWorkspace('browser');
  }, []);

  const closeWorkspace = useCallback(() => {
    setWorkspace(null);
    setBrowserUrl(null);
  }, []);

  if (!isLoggedIn()) return <Navigate to="/login" replace state={{ from: location }} />;
  return (
    <WorkspaceContext.Provider value={{ workspace, browserUrl, openWorkspace, openBrowser, closeWorkspace }}>
      <FirstRunExperience>
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </Layout>
      </FirstRunExperience>
    </WorkspaceContext.Provider>
  );
}

/**
 * Admin-only routes. Users see a redirect to dashboard.
 */
function RequireAdmin() {
  const user = getUser();
  if (!user || (user.role !== 'admin' && user.role !== 'owner')) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

function RequireProduct({ product }: { product: 'bos' | 'hos' }) {
  const { availableProducts, loading } = useProduct();
  if (loading) return <PageLoader />;
  if (!availableProducts.includes(product)) {
    return <Navigate to={product === 'hos' ? '/' : '/hos'} replace />;
  }
  if (product === 'hos') {
    const user = getUser();
    if (!user || (user.role !== 'admin' && user.role !== 'owner')) {
      if (availableProducts.includes('bos')) return <Navigate to="/" replace />;
      return (
        <main className="grid min-h-[60vh] place-items-center p-8 text-center">
          <div className="max-w-md rounded-2xl border border-border bg-surface-2/85 p-7">
            <h1 className="text-xl font-semibold text-text-primary">HOS owner access required</h1>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              This private Home Operating System is available only to its owner and approved administrators.
            </p>
          </div>
        </main>
      );
    }
  }
  return <Outlet />;
}

function RequireHosModule({ moduleId }: { moduleId: string }) {
  const { loading, moduleEnabled } = useProduct();
  if (loading) return <PageLoader />;
  if (!moduleEnabled(moduleId)) return <Navigate to="/hos" replace />;
  return <Outlet />;
}

function RequireAnyHosModule({ moduleIds }: { moduleIds: string[] }) {
  const { loading, moduleEnabled } = useProduct();
  if (loading) return <PageLoader />;
  if (!moduleIds.some((moduleId) => moduleEnabled(moduleId))) return <Navigate to="/hos" replace />;
  return <Outlet />;
}

function RequireIntegration({ integrationId }: { integrationId: string }) {
  const { loading, integrationEnabled } = useProduct();
  if (loading) return <PageLoader />;
  if (!integrationEnabled(integrationId)) return <Navigate to="/" replace />;
  return <Outlet />;
}

function RequireStandaloneHosWhatsapp() {
  const { config, loading } = useProduct();
  if (loading) return <PageLoader />;
  if (config.edition !== 'hos') {
    return <Navigate to={hosWhatsappManagementPath(config.edition)} replace />;
  }
  return <Outlet />;
}

function LegacyHealthRedirect({ suffix = '' }: { suffix?: string }) {
  const { availableProducts, loading, moduleEnabled } = useProduct();
  if (loading) return <PageLoader />;
  if (!availableProducts.includes('hos') || !moduleEnabled('health')) return <Navigate to="/" replace />;
  return <Navigate to={`/hos/health${suffix}`} replace />;
}

/** /rascals/:handle -> /client-managers/:handle, preserving the handle. */
function LegacyRascalRedirect() {
  const { handle } = useParams();
  return <Navigate to={handle ? `/client-managers/${handle}` : '/client-managers'} replace />;
}

function ProductRoot() {
  const { activeProduct } = useProduct();
  const user = getUser();
  const canUseHos = user?.role === 'admin' || user?.role === 'owner';
  return activeProduct === 'hos' && canUseHos ? <Navigate to="/hos" replace /> : <Dashboard />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<Login />} />
        <Route path="/join/:code" element={<JoinMeeting />} />

        {/* Authenticated routes */}
        <Route element={<RequireAuth />}>
          {/* Legacy BOS routes remain stable for BOS and combined editions. */}
          <Route element={<RequireProduct product="bos" />}>
            <Route path="/" element={<ProductRoot />} />
            <Route path="/office" element={<Office />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/kanban" element={<TaskBoard />} />
            <Route path="/tasks" element={<TaskBoard />} />
            <Route path="/board" element={<Board />} />
            <Route element={<RequireIntegration integrationId="miro" />}>
              <Route path="/canvas" element={<Canvas />} />
            </Route>
            <Route path="/paperclip" element={<Paperclip />} />
            <Route path="/crm" element={<CRM />} />
            <Route path="/signal-desk" element={<SignalDesk />} />
            <Route element={<RequireIntegration integrationId="linkedin" />}>
              <Route path="/linkedin" element={<LinkedIn />} />
              <Route path="/social" element={<Navigate to="/linkedin" replace />} />
            </Route>
            <Route element={<RequireIntegration integrationId="whatsapp" />}>
              <Route path="/whatsapp" element={<WhatsApp />} />
            </Route>
            <Route path="/oc" element={<OC />} />
            <Route path="/coo" element={<COO />} />
            <Route path="/client-managers" element={<Rascals />} />
            <Route path="/client-managers/:handle" element={<RascalWorkspace />} />
            {/* The surface was served at /rascals until 2.1.0. Redirect rather
                than drop it: existing bookmarks and any link already sent to a
                client would otherwise 404. */}
            <Route path="/rascals" element={<Navigate to="/client-managers" replace />} />
            <Route path="/rascals/:handle" element={<LegacyRascalRedirect />} />
          </Route>

          {/* HOS personal surfaces share the shell but keep a separate route namespace. */}
          <Route element={<RequireProduct product="hos" />}>
            <Route path="/hos" element={<HosDashboard />} />
            <Route path="/hos/assistant" element={<HosAssistant />} />
            <Route element={<RequireHosModule moduleId="social" />}>
              <Route path="/hos/social" element={<HosSocial />} />
            </Route>
            <Route element={<RequireHosModule moduleId="address-book" />}>
              <Route path="/hos/address-book" element={<HosAddressBook />} />
            </Route>
            <Route element={<RequireAnyHosModule moduleIds={['social', 'address-book']} />}>
              <Route element={<RequireIntegration integrationId="whatsapp" />}>
                <Route element={<RequireStandaloneHosWhatsapp />}>
                  <Route path="/hos/whatsapp" element={<WhatsApp />} />
                </Route>
              </Route>
            </Route>
            <Route element={<RequireHosModule moduleId="health" />}>
              <Route path="/hos/health" element={<Health />} />
              <Route path="/hos/health/holo" element={<HealthHolo />} />
              <Route path="/hos/health/journal" element={<HealthJournal />} />
              <Route path="/hos/health/records" element={<HealthMedicalRecords />} />
            </Route>
            <Route element={<RequireHosModule moduleId="fitness" />}>
              <Route path="/hos/fitness" element={<HosFitness />} />
            </Route>
            <Route element={<RequireHosModule moduleId="nutrition" />}>
              <Route path="/hos/nutrition" element={<HosNutrition />} />
            </Route>
            <Route element={<RequireHosModule moduleId="finances" />}>
              <Route path="/hos/finances" element={<HosFinances />} />
            </Route>
            <Route element={<RequireHosModule moduleId="spiritual" />}>
              <Route path="/hos/spiritual" element={<HosSpiritual />} />
            </Route>
          </Route>

          <Route path="/health" element={<LegacyHealthRedirect />} />
          <Route path="/health/holo" element={<LegacyHealthRedirect suffix="/holo" />} />
          <Route path="/health/journal" element={<LegacyHealthRedirect suffix="/journal" />} />
          <Route path="/health/records" element={<LegacyHealthRedirect suffix="/records" />} />

          {/* Admin-only system routes */}
          <Route element={<RequireAdmin />}>
            <Route path="/settings"     element={<Settings />} />
            <Route path="/settings/:section" element={<Navigate to="/settings" replace />} />
            <Route element={<RequireProduct product="bos" />}>
              <Route path="/agents"            element={<EmployeeAgents />} />
              <Route path="/agents/:handle"    element={<AgentWorkspace />} />
              <Route path="/admin"        element={<Navigate to="/settings" replace />} />
              <Route path="/hermes"       element={<Navigate to="/settings" replace />} />
              <Route path="/chief"        element={<Navigate to="/settings" replace />} />
              <Route path="/voice"        element={<Navigate to="/settings" replace />} />
              <Route path="/brain"        element={<Navigate to="/settings" replace />} />
              <Route path="/connectors"   element={<Navigate to="/settings" replace />} />
              <Route path="/learning"     element={<Navigate to="/settings" replace />} />
              <Route path="/self-healing" element={<Navigate to="/settings" replace />} />
              <Route path="/backup"       element={<Navigate to="/settings" replace />} />
            </Route>
          </Route>
        </Route>

        {/* Catch-all → login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}
